import { useCallback, useMemo, useState } from "react"
import { GALLERY_STUDIO_INTENT_TARGET_LIMIT } from "@nova-ai/shared/gallery-studio"

import type { GalleryStudioBootstrapInputs } from "../services/use-gallery-studio"

type BootstrapCandidate = GalleryStudioBootstrapInputs["candidates"][number]
type BootstrapStep = "intro" | "select" | "confirm"

export function useGalleryStudioBootstrap(
  candidates: BootstrapCandidate[],
  onCreate: (targetSimpleSkus: string[]) => Promise<unknown>
) {
  const [step, setStep] = useState<BootstrapStep>("intro")
  const [selectedSkus, setSelectedSkus] = useState(() =>
    candidates.map((candidate) => candidate.simpleSku)
  )
  const selectedSkuSet = useMemo(() => new Set(selectedSkus), [selectedSkus])
  const selectedCandidates = useMemo(
    () => candidates.filter((candidate) => selectedSkuSet.has(candidate.simpleSku)),
    [candidates, selectedSkuSet]
  )
  const selectionError = selectionValidationMessage(selectedSkus.length)

  const toggleSku = useCallback((simpleSku: string) => {
    setSelectedSkus((current) =>
      current.includes(simpleSku)
        ? current.filter((selectedSku) => selectedSku !== simpleSku)
        : [...current, simpleSku]
    )
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedSkus((current) =>
      current.length === candidates.length ? [] : candidates.map((candidate) => candidate.simpleSku)
    )
  }, [candidates])

  const reviewSelection = useCallback(() => {
    if (!selectionError) {
      setStep("confirm")
    }
  }, [selectionError])

  const confirmCreate = useCallback(() => {
    if (selectionError) {
      return
    }
    // TanStack Mutation stores the rejection for the inline error state. Catch
    // the returned promise so a click never creates an unhandled rejection.
    void onCreate(selectedSkus).catch(() => undefined)
  }, [onCreate, selectedSkus, selectionError])

  const goBack = useCallback(() => {
    setStep((current) => (current === "confirm" ? "select" : "intro"))
  }, [])

  return {
    step,
    selectedSkus,
    selectedSkuSet,
    selectedCandidates,
    selectionError,
    start: () => setStep("select"),
    toggleSku,
    toggleAll,
    reviewSelection,
    confirmCreate,
    goBack,
  }
}

function selectionValidationMessage(selectedCount: number): string | null {
  if (selectedCount === 0) {
    return "Select at least one variant."
  }
  if (selectedCount > GALLERY_STUDIO_INTENT_TARGET_LIMIT) {
    return `Select at most ${GALLERY_STUDIO_INTENT_TARGET_LIMIT} variants. Deselect ${selectedCount - GALLERY_STUDIO_INTENT_TARGET_LIMIT} to continue.`
  }
  return null
}
